import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { FileUp, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
// Bundle the worker locally so it always matches the installed pdfjs-dist version
// (cdnjs doesn't always have the exact v5.x .mjs worker, which causes uploads to silently fail).
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface DocumentToBlogProps {
  onContentExtracted: (data: {
    title: string;
    content: string;
    excerpt: string;
  }) => void;
}

const DocumentToBlog = ({ onContentExtracted }: DocumentToBlogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const extractTitleFromContent = (content: string): { title: string; remainingContent: string } => {
    // Try to extract title from first H1 or first line
    const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
    if (h1Match) {
      const title = h1Match[1].replace(/<[^>]*>/g, '').trim();
      const remainingContent = content.replace(h1Match[0], '').trim();
      return { title, remainingContent };
    }

    // Try first strong/bold text
    const strongMatch = content.match(/<(strong|b)[^>]*>(.*?)<\/\1>/i);
    if (strongMatch) {
      const potentialTitle = strongMatch[2].replace(/<[^>]*>/g, '').trim();
      if (potentialTitle.length < 100) {
        return { title: potentialTitle, remainingContent: content };
      }
    }

    // Fall back to first line of text
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    const firstLine = textContent.split('\n')[0].trim();
    if (firstLine.length < 100) {
      return { title: firstLine, remainingContent: content };
    }

    return { title: 'Untitled Post', remainingContent: content };
  };

  const generateExcerpt = (htmlContent: string, maxLength: number = 200): string => {
    // Strip HTML tags and get plain text
    const textContent = htmlContent
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    if (textContent.length <= maxLength) {
      return textContent;
    }
    
    // Find the last space before maxLength to avoid cutting words
    const truncated = textContent.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return truncated.substring(0, lastSpace) + '...';
  };

  const processDocx = async (file: File): Promise<{ title: string; content: string; excerpt: string }> => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    
    const htmlContent = result.value;
    const { title, remainingContent } = extractTitleFromContent(htmlContent);
    const excerpt = generateExcerpt(remainingContent);
    
    return {
      title,
      content: remainingContent,
      excerpt
    };
  };

  const processPdf = async (file: File): Promise<{ title: string; content: string; excerpt: string }> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    interface TextBlock {
      text: string;
      fontSize: number;
      isBold: boolean;
      y: number;
    }
    
    const allBlocks: TextBlock[] = [];
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      
      // Group text items by their Y position (same line)
      const lineMap = new Map<number, { text: string; fontSize: number; isBold: boolean }>();
      
      textContent.items.forEach((item: any) => {
        if (!item.str?.trim()) return;
        
        const y = Math.round(item.transform[5]); // Y position
        const fontSize = item.height || 12;
        const fontName = item.fontName || '';
        const isBold = fontName.toLowerCase().includes('bold') || fontName.includes('Bold');
        
        if (lineMap.has(y)) {
          const existing = lineMap.get(y)!;
          existing.text += ' ' + item.str;
          existing.fontSize = Math.max(existing.fontSize, fontSize);
          existing.isBold = existing.isBold || isBold;
        } else {
          lineMap.set(y, { text: item.str, fontSize, isBold });
        }
      });
      
      // Convert to blocks sorted by Y position (top to bottom)
      const sortedLines = Array.from(lineMap.entries())
        .sort((a, b) => b[0] - a[0]) // Higher Y = top of page
        .map(([y, data]) => ({ ...data, y }));
      
      allBlocks.push(...sortedLines);
    }
    
    if (allBlocks.length === 0) {
      return { title: 'Untitled Post', content: '', excerpt: '' };
    }
    
    // Calculate average font size to detect headings
    const avgFontSize = allBlocks.reduce((sum, b) => sum + b.fontSize, 0) / allBlocks.length;
    const maxFontSize = Math.max(...allBlocks.map(b => b.fontSize));
    
    // Convert blocks to HTML with heading detection
    const htmlParts: string[] = [];
    
    allBlocks.forEach((block, index) => {
      const text = block.text.trim();
      if (!text) return;
      
      const isShortLine = text.length < 100;
      const isLargeFont = block.fontSize > avgFontSize * 1.3;
      const isVeryLargeFont = block.fontSize >= maxFontSize * 0.9;
      const isFirstBlock = index === 0;
      
      // Determine heading level
      if (isVeryLargeFont && isShortLine) {
        htmlParts.push(`<h1>${text}</h1>`);
      } else if (isLargeFont && isShortLine && block.isBold) {
        htmlParts.push(`<h2>${text}</h2>`);
      } else if ((isLargeFont || block.isBold) && isShortLine && !isFirstBlock) {
        htmlParts.push(`<h3>${text}</h3>`);
      } else if (block.isBold) {
        htmlParts.push(`<p><strong>${text}</strong></p>`);
      } else {
        htmlParts.push(`<p>${text}</p>`);
      }
    });
    
    const htmlContent = htmlParts.join('\n');
    const { title, remainingContent } = extractTitleFromContent(htmlContent);
    const excerpt = generateExcerpt(remainingContent);

    return {
      title,
      content: remainingContent,
      excerpt
    };
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isDocx = fileName.endsWith('.docx');
    const isPdf = fileName.endsWith('.pdf');

    if (!isDocx && !isPdf) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a DOCX or PDF file',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);

    try {
      let result;
      
      if (isDocx) {
        result = await processDocx(file);
      } else {
        result = await processPdf(file);
      }

      onContentExtracted(result);
      
      toast({
        title: 'Document converted',
        description: 'Content has been extracted and populated into the form'
      });
    } catch (error) {
      console.error('Error processing document:', error);
      toast({
        title: 'Conversion failed',
        description: 'Failed to extract content from the document. Please try again or use a different file.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx,.pdf"
        onChange={handleFileSelect}
        className="hidden"
        id="document-upload"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isProcessing}
        onClick={() => fileInputRef.current?.click()}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Converting...
          </>
        ) : (
          <>
            <FileUp className="w-4 h-4 mr-2" />
            Import from Document
          </>
        )}
      </Button>
    </div>
  );
};

export default DocumentToBlog;
